<i18n src="./resources/crm/contact-locales.json"></i18n>

<template>
  <div class="crm-contact-container">
    <div v-show="!isLoading" class="inset-fade-effect" :dark="$vuetify.theme.dark" />
    <div v-if="isLoading" class="crm-contact-loading">
      <v-progress-linear color="var(--v-accent-base)" indeterminate height="4"></v-progress-linear>
      <div v-once class="text-center pa-5 opacity-75">{{ $t('loading') }}</div>
    </div>
    <div v-else-if="settings.length" class="crm-contact-settings">
      <div v-for="item in settings" :key="item.standardFieldName">
        <!-- TEXT -->
        <v-text-field
          v-if="item.fieldType === 'Text'"
          v-model="item.value"
          type="text"
          :counter="item.maxLength"
          :hide-details="!item.editable"
          :label="item.customFieldName"
          :maxlength="item.maxLength"
          :readonly="!item.editable"
          @change="updateItem(item)"
        >
          <template slot="append">
            <v-icon class="action-btn" @click="handleAction(item)">
              {{ getActionIcon(item) }}
            </v-icon>
            <v-icon class="action-btn" @click="handleSmsAction(item)">
              {{ getSmsActionIcon(item) }}
            </v-icon>
            <v-tooltip top z-index="9999">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  class="action-btn"
                  v-on="on"
                  @click="copyToClipboard(item.customFieldName, item.value)"
                >
                  mdi-content-copy
                </v-icon>
              </template>
              <span>{{ $t('copy', { fieldName: item.customFieldName }) }}</span>
            </v-tooltip>
          </template>
        </v-text-field>

        <!-- PERCENTAGE -->
        <v-text-field
          v-if="item.fieldType === 'Percentage'"
          v-model="item.value"
          type="number"
          :hide-details="!item.editable"
          :label="item.customFieldName"
          :maxlength="item.maxLength"
          :readonly="!item.editable"
          :rules="getRules(item)"
          @change="updateItem(item)"
        >
          <template slot="append">
            <v-icon v-if="item.fieldType === 'Percentage'">mdi-percent-outline</v-icon>
          </template>
        </v-text-field>

        <!-- TIMEZONE -->
        <v-autocomplete
          v-if="item.fieldType === 'TimeZonesValue'"
          v-model="item.value"
          class="dropdown"
          item-text="standardName"
          :hide-details="!item.editable"
          :label="item.customFieldName"
          :items="item.items"
          :maxlength="item.maxLength"
          :menu-props="{ contentClass: 'custom-scrollbar' }"
          :disabled="!item.predefinedValueList.length"
          editable
          :readonly="!item.editable"
          :return-object="item.returnObject || false"
          @change="updateItem(item)"
        />

        <!-- FLAG -->
        <EcsCustomInput
          v-if="item.fieldType === 'Flag'"
          class="custom-input"
          type="flag"
          :value="item.value"
          :disabled="!item.editable"
          :label="item.customFieldName"
          @change="updateItem(item)"
        />
        <!-- -->

        <!-- DateTime -->
        <EcsCustomInput
          v-if="item.fieldType === 'DateTime'"
          v-model="item.value"
          class="custom-input"
          type="date"
          format="mm-dd-yyyy"
          :clearable="true"
          :disabled="!item.editable"
          :label="item.customFieldName"
          :min="getMinDate"
          :placeholder="$t('date')"
          @change="updateItem(item)"
        />
        <!-- -->

        <!-- Timespan -->
        <EcsCustomInput
          v-if="item.fieldType === 'Timespan'"
          v-model="item.value"
          class="custom-input"
          type="time"
          :format="applicationSettings.timeFormat"
          :clearable="true"
          :label="item.customFieldName"
          :disabled="!item.editable"
          @change="updateItem(item)"
        />
        <!-- -->
      </div>
    </div>
    <div v-else class="crm-no-contact-info" :dark="$vuetify.theme.dark">
      <div v-once>{{ $t('noContactInfo') }}</div>
      <div>
        <v-btn color="primary" @click="assignContactToInteraction">
          <span v-once>{{ $t('assignContactToInteraction') }}</span>
        </v-btn>
      </div>
      <div>
        <a class="crm-contact-interaction-link" @click="linkContactToInteraction">
          {{ $t('linkContactToInteraction') }}
        </a>
      </div>
    </div>
    <InteractionCrmContactSelectionDialog
      ref="crmContactSelectionDialog"
      :business-process-id="selectedBusinessProcessId"
      @contact-selected="handleContactAssignment"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import InteractionService from '@/services/interactionService';
import InteractionCrmContactSelectionDialog from '@/components/ecs-interaction/InteractionCrmContactSelectionDialog';

const UNDEFINED_DATE_TIME = '0001-01-01T00:00:00.000+00:00';

export default {
  name: 'InteractionCrmContactSettings',

  components: { InteractionCrmContactSelectionDialog },

  props: {
    interaction: {
      type: Object,
      default: () => ({})
    },

    contactId: {
      type: String,
      default: ''
    },

    businessProcessId: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      contactData: null,
      isLoading: true
    };
  },

  computed: {
    ...mapGetters('AGENT', ['agentState']),

    ...mapGetters('CONTACTS', ['getCrmContact', 'getContactV2']),

    ...mapGetters('SETTINGS', ['applicationSettings', 'agentSettings']),

    ...mapGetters('REFERENCES', ['availableTimeZones']),

    getMinDate() {
      const date = new Date(0);
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    },

    selectedBusinessProcessId() {
      return (
        this.businessProcessId ||
        (this.interaction && this.interaction.routing && this.interaction.routing.businessProcessId) ||
        null
      );
    },

    selectedContactId() {
      return this.contactId || (this.interaction && this.interaction.crm && this.interaction.crm.contactId) || null;
    },

    contact() {
      return this.getContactV2(this.selectedContactId);
    },

    settings() {
      if (!this.contactData) return [];

      const settings = Object.values(this.contactData)
        .filter(item => item.visible)
        .sort((i1, i2) => i1.order - i2.order);
      settings.forEach(setting => {
        if (setting.fieldType === 'DateTime') {
          if (setting.value === UNDEFINED_DATE_TIME || !setting.value) {
            setting.value = '';
            return;
          }
          const format = setting.editable ? 'YYYY-MM-DD' : setting.format;
          setting.value = moment.utc(setting.value).format(format);
        } else if (setting.fieldType === 'Timespan') {
          setting.value = moment(setting.value, this.applicationSettings.timeFormat);
        } else if (setting.fieldType === 'TimeZonesValue') {
          setting.items = this.availableTimeZones.filter(tz => {
            return setting.predefinedValueList.includes(tz.standardName);
          });
        }
      });
      return settings;
    }
  },

  watch: {
    selectedBusinessProcessId() {
      this.getContactData();
    },

    selectedContactId() {
      this.getContactData();
    }
  },

  created() {
    this.getContactData();
  },

  methods: {
    ...mapActions('CONTACTS', ['updateCrmContact', 'requestCrmTemplate']),

    ...mapActions('INTERACTIONS', ['createInteractionCrmContact', 'assignInteractionCrmContact']),

    ...mapActions('INTERACTIONS/emails', ['createOutgoingEmail']),

    getContactData() {
      this.isLoading = true;

      if (!this.selectedContactId || !this.selectedBusinessProcessId) {
        this.isLoading = false;
        this.contactData = null;
        return;
      }

      this.requestCrmTemplate({
        contactId: this.selectedContactId,
        businessProcessId: this.selectedBusinessProcessId
      })
        .then(response => {
          this.contactData = response.data;
        })
        .catch(() => {
          this.$root.$emit('snackbar-message', {
            message: this.$t('errors.unableToRetrieveContactData'),
            type: 'error'
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    getRules(item) {
      const rules = [];
      const { isRequired, format, minLength, maxLength, minValue, maxValue } = item;

      if (isRequired) {
        rules.push(value => !!value || this.$t('validation.required'));
      }

      if (format) {
        const regex = new RegExp(format);
        rules.push(value => regex.test(value) || this.$t('validation.format'));
      }

      if (minLength) {
        if (isRequired) {
          rules.push(value => {
            return (value || '').length >= minLength || this.$t('validation.minLength', { minLength });
          });
        } else {
          rules.push(value => {
            const input = value || '';
            return (
              input.length === 0 ||
              input === '' ||
              input.length >= minLength ||
              this.$t('validation.minLength', { minLength })
            );
          });
        }
      }

      if (maxLength) {
        rules.push(value => {
          if (typeof value !== 'string') {
            return true;
          }
          return (value || '').length <= maxLength || this.$t('validation.maxLength', { maxLength });
        });
      }

      const isNumeric = value => {
        return /^[-+]?\d+(\.?\d+)?$/.test(value);
      };

      if (minValue) {
        rules.push(value => isNumeric(value) || this.$t('validation.minValueNaN', { value }));
        rules.push(
          value =>
            parseFloat(value) >= minValue || parseInt(value) >= minValue || this.$t('validation.minValue', { minValue })
        );
      }

      if (maxValue) {
        rules.push(value => isNumeric(value) || this.$t('validation.maxValueNaN', { value }));
        rules.push(
          value =>
            parseFloat(value) <= maxValue || parseInt(value) >= maxValue || this.$t('validation.maxValue', { maxValue })
        );
      }

      if (this.isTelephonyEndPoint(item.standardFieldName) && item.value) {
        rules.push(value => this.isDestinationNotEmpty(value) || this.$t('validation.invalidPhoneNumber'));
      }

      return rules;
    },

    updateItem(item) {
      if (!this.isValid(item)) return;

      if (!this.selectedContactId) return;

      const itemValue =
        item.fieldType === 'Flag'
          ? !item.value
          : item.fieldType === 'Timespan'
          ? moment(item.value, this.applicationSettings.timeFormat).format('HH:mm')
          : item.value;

      this.updateCrmContact({
        contactId: this.selectedContactId,
        properties: {
          [item.standardFieldName]: itemValue
        }
      })
        .then(() => {
          this.contactData[item.standardFieldName].value = itemValue;
        })
        .catch(() => {
          this.$root.$emit('snackbar-message', {
            message: this.$t('errors.unableToUpdateContactProperties'),
            type: 'error'
          });
        });
    },

    assignContactToInteraction() {
      this.isLoading = true;
      this.createInteractionCrmContact(this.interaction).catch(() => {
        this.isLoading = false;
        this.$root.$emit('snackbar-message', {
          message: this.$t('errors.unableToAssignContact'),
          type: 'error'
        });
      });
    },

    isValid(item) {
      const rules = this.getRules(item);
      return rules.every(rule => rule(item.value) === true);
    },

    getActionIcon(item) {
      let result;

      if (this.isTelephonyEndPoint(item.standardFieldName) && this.isDestinationNotEmpty(item.value)) {
        result = 'mdi-phone';
      } else if (this.isEmailEndPoint(item.standardFieldName) && item.value) {
        result = 'mdi-email';
      }

      return result;
    },

    getSmsActionIcon(item) {
      let result;
      if (this.isTelephonyEndPoint(item.standardFieldName) && this.isDestinationNotEmpty(item.value)) {
        result = 'mdi-chat-processing-outline';
      }

      return result;
    },

    handleAction(item) {
      if (!this.isValid(item) || !item.value) return;

      this.$root.$emit('close-contact-card-actions');

      if (this.isTelephonyEndPoint(item.standardFieldName)) {
        this.dial(item.value);
      } else if (this.isEmailEndPoint(item.standardFieldName)) {
        this.sendEmail(item.value);
      }
    },

    handleSmsAction(item) {
      if (!this.isValid(item) || !item.value) return;

      this.$root.$emit('close-contact-card-actions');

      if (this.isTelephonyEndPoint(item.standardFieldName)) {
        this.sendSms(item.value);
      }
    },

    isTelephonyEndPoint(propertyName = '') {
      return ['phone1', 'phone2', 'phone3', 'phone4', 'phone5'].includes(propertyName.toLowerCase());
    },

    isEmailEndPoint(propertyName) {
      return ['email'].includes(propertyName.toLowerCase());
    },

    isDestinationNotEmpty(value) {
      return !!value;
    },

    async dial(phoneNumber) {
      const errorMessage = this.$t('errors.unableToCall', { destination: phoneNumber });

      if (!phoneNumber) {
        this.emitErrorMessage(errorMessage);
        return;
      }

      if (this.$router.currentRoute.name !== 'Calls') {
        await this.$router.push('calls');
      }

      this.$root.$emit('open-dialpad-number', {
        number: phoneNumber
      });
    },

    sendEmail(email) {
      const errorMessage = `${this.$t('errors.unableToEmail', { name: this.contact.name, email })}`;
      const emailSignatureErrorMessage = this.$t('errors.unableToGetEmailSignature');

      const businessProcessId = this.agentSettings.defaultOutgoingEmailBusinessProcessId;
      const contactId = this.selectedContactId;

      this.createOutgoingEmail({ businessProcessId, contactId, destination: email })
        .then(data => {
          if (!data.outgoingEmailData.signature) {
            this.$root.$emit('snackbar-message', {
              message: emailSignatureErrorMessage,
              type: 'error'
            });
          }
          if (!/emails/i.test(this.$route.name)) this.$router.push('emails');
        })
        .catch(() => {
          this.$root.$emit('snackbar-message', {
            message: errorMessage,
            type: 'error'
          });
        });
    },

    sendSms(phoneNumber) {
      const businessProcessId = this.agentSettings.defaultOutgoingSmsBusinessProcessId;

      const errorMessage = this.$t('errors.unableToSms', { destination: phoneNumber });
      InteractionService.startSMSInteraction({
        businessProcessId,
        destination: phoneNumber
      })
        .then(({ data }) => {
          if (!data) {
            this.$root.$emit('snackbar-message', { message: errorMessage, type: 'error' });
          }
        })
        .catch(() => {
          this.$root.$emit('snackbar-message', { message: errorMessage, type: 'error' });
        });
    },

    linkContactToInteraction() {
      this.$refs.crmContactSelectionDialog.open();
    },

    handleContactAssignment(contact) {
      this.isLoading = true;
      this.assignInteractionCrmContact({ interaction: this.interaction, contactId: contact.id }).catch(() => {
        this.$root.$emit('snackbar-message', {
          message: this.$t('errors.unableToAssignContact'),
          type: 'error'
        });
      });
    },

    copyToClipboard(label, value) {
      navigator.clipboard
        .writeText(value || '')
        .then(() => {
          this.$root.$emit('snackbar-message', {
            message: this.$t('copySuccess', { label }),
            type: 'success'
          });
        })
        .catch(() => {});
    }
  }
};
</script>

<style scoped>
.crm-contact-container {
  height: 100%;
  overflow-y: auto;
}

.crm-contact-settings {
  width: 100%;
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 0.8fr));
  grid-gap: 0 24px;
  justify-content: center;
}

.crm-no-contact-info {
  border-radius: 8px !important;
  border: 2px dashed rgba(0, 0, 0, 0.5);
  color: rgba(0, 0, 0, 0.5);
  padding: 16px;
  margin: 16px;
  text-align: center;
  width: calc(100% - 32px);
  align-self: center;
  justify-self: center;
}

[dark].crm-no-contact-info {
  border: 2px dashed rgba(255, 255, 255, 0.5);
  color: rgba(255, 255, 255, 0.5);
}

.crm-no-contact-info > div:not(:first-child) {
  margin-top: 16px;
}

.crm-contact-interaction-link {
  text-decoration: underline;
}

.action-btn {
  transition: all 0.2s ease;
  opacity: 0.5;
  margin-right: 4px;
}

.action-btn:hover {
  opacity: 1;
}
</style>
